import { requestApiJson } from './client';
import type { components } from './types.gen';

export type ChatQuestion = components['schemas']['ChatQuestion'];
export type ChatQuestionMyResponse = components['schemas']['ChatQuestionMyResponse'];
export type ChatQuestionResponseRow = components['schemas']['ChatQuestionResponseRow'];
export type ChatQuestionResponseSummary = components['schemas']['ChatQuestionResponseSummary'];
export type ChatQuestionResponsePage = components['schemas']['ChatQuestionResponsePage'];
export type PendingChatQuestion = components['schemas']['PendingChatQuestion'];

export type ChatQuestionCreateInput = {
  scope: ChatQuestion['scope'];
  artistId?: string | null;
  eventId?: string | null;
  groupThreadId?: string | null;
  supplementalMessage?: string | null;
  questionText: string;
  options: string[];
  responseDeadlineAt: string;
};

export type ChatQuestionCreatedPayload = {
  success: boolean;
  messageId: string;
  question: ChatQuestion;
};

export type PendingChatQuestionsPayload = {
  success: boolean;
  count: number;
  items: PendingChatQuestion[];
};

export type ChatQuestionResponsesPayload = {
  success?: boolean;
  question: ChatQuestion;
  responses: ChatQuestionResponseRow[];
  summary: ChatQuestionResponseSummary;
  page: ChatQuestionResponsePage;
};

export type ChatQuestionAnswerPayload = {
  success: boolean;
  response: ChatQuestionMyResponse;
};

const enc = (value: unknown) => encodeURIComponent(String(value || ''));

const errorMessage = (value: unknown, fallback: string): string => {
  if (value && typeof value === 'object' && 'message' in value) {
    const message = String((value as { message?: unknown }).message || '').trim();
    if (message) return message;
  }
  return fallback;
};

const unwrap = async <T>(
  request: ReturnType<typeof requestApiJson<T>>,
  fallback: string,
): Promise<T> => {
  const { data, response } = await request;
  if (!response.ok || data == null) {
    throw new Error(errorMessage(data, fallback));
  }
  return data;
};

export function sendChatQuestion({
  scope,
  artistId,
  eventId,
  groupThreadId,
  supplementalMessage,
  questionText,
  options,
  responseDeadlineAt,
  topicId,
  newTopic,
}: ChatQuestionCreateInput & { topicId?: string; newTopic?: unknown }): Promise<ChatQuestionCreatedPayload> {
  let path: string;
  if (scope === 'event') {
    path = `/events/${enc(eventId)}/messages/questions`;
  } else if (scope === 'group') {
    path = `/admin/group-chat/threads/${enc(groupThreadId)}/messages/questions`;
  } else {
    path = `/admin/messages/${enc(artistId)}/questions`;
  }
  return unwrap(requestApiJson<ChatQuestionCreatedPayload>(path, {
    method: 'POST',
    body: {
      topicId,
      newTopic,
      supplementalMessage: String(supplementalMessage || '').trim() || null,
      questionText: String(questionText || '').trim(),
      options,
      responseDeadlineAt,
    },
  }), '質問メッセージの送信に失敗しました。');
}

export function answerChatQuestion(
  questionId: string,
  optionId: string,
): Promise<ChatQuestionAnswerPayload> {
  return unwrap(requestApiJson<ChatQuestionAnswerPayload>(`/artist/chat-questions/${enc(questionId)}/responses`, {
    method: 'POST',
    body: { optionId },
  }), '回答を登録できませんでした。');
}

export function listPendingChatQuestions({
  limit = 50,
  signal,
}: {
  limit?: number;
  signal?: AbortSignal;
} = {}): Promise<PendingChatQuestionsPayload> {
  return unwrap(requestApiJson<PendingChatQuestionsPayload>('/artist/chat-questions/pending', {
    query: { limit },
    signal,
    cache: 'no-store',
  }), '未回答の質問メッセージを取得できませんでした。');
}

export function listChatQuestionResponses(
  question: ChatQuestion,
): Promise<ChatQuestionResponsesPayload> {
  let path: string;
  if (question?.scope === 'event') {
    path = `/events/${enc(question.event_id)}/messages/questions/${enc(question.id)}/responses`;
  } else if (question?.scope === 'group') {
    path = `/admin/group-chat/threads/${enc(question.thread_id)}/messages/questions/${enc(question.id)}/responses`;
  } else {
    path = `/admin/messages/${enc(question?.artist_id)}/questions/${enc(question?.id)}/responses`;
  }
  return unwrap(requestApiJson<ChatQuestionResponsesPayload>(path, {
    query: { limit: 500, offset: 0 },
    cache: 'no-store',
  }), '回答状況を取得できませんでした。');
}
